import { createI18n } from 'vue-i18n'
import en from '../locales/en.json'
import fr from '../locales/fr.json'
import de from '../locales/de.json'
import it from '../locales/it.json'
import { useUserStore } from '~~/store/user'
import { Locales } from '~~/types/types'

export default defineNuxtPlugin(async ({ vueApp }) => {
  const appConfig = useAppConfig()
  const availableLocales = appConfig.languages

  const tenant = useTenant()

  /**
   * The per-tenant default locale (configured on the tenant doc, 'de' for
   * sjmuz). Used as the initial locale and as the fallback for unknown strings,
   * replacing the previous hard-coded 'en'. Validated against availableLocales.
   */
  function tenantDefault(): Locales {
    const loc = tenant.value.defaultLocale
    return (availableLocales.includes(loc) ? loc : 'de') as Locales
  }

  /**
   * Normalizes a locale string (e.g. a user's stored profile locale) to one of
   * the available locales, falling back to the tenant default.
   */
  function normalizeLocale(str: string): Locales {
    if (availableLocales.includes(str)) return str as Locales
    const match = str.match(/^(\w\w)-?/)
    if (match && availableLocales.includes(match[1].toLowerCase())) {
      return match[1].toLowerCase() as Locales
    }
    return tenantDefault()
  }

  // The initial locale comes from the tenant's configured default, NOT the
  // browser language. A logged-in user's profile locale overrides it once the
  // user doc loads (see subscribeToLocaleChange below).
  const initialLocale = tenantDefault()
  console.debug(`Set locale to ${initialLocale}`)

  const i18nDef = createI18n({
    legacy: false,
    globalInjection: true,
    locale: initialLocale,
    messages: {
      de,
      fr,
      it,
      en,
    },
  })
  vueApp.use(i18nDef)

  // The tenant doc resolves asynchronously and client-side, so its defaultLocale
  // may only become available after this plugin runs. Apply it once resolved —
  // but never clobber a locale that came from a logged-in user's profile.
  let localeFromProfile = false
  watch(
    () => tenant.value.defaultLocale,
    (loc) => {
      if (localeFromProfile) return
      const next = (availableLocales.includes(loc) ? loc : 'de') as Locales
      i18nDef.global.locale.value = next
      console.debug(`Set locale from tenant default to ${next}`)
    },
  )

  const userStore = useUserStore()
  userStore.subscribeToLocaleChange((targetLocale: string | null) => {
    // Only an explicit profile locale takes precedence over the tenant default;
    // an empty/missing one must not lock out the tenant watcher above.
    if (targetLocale) localeFromProfile = true
    const next = normalizeLocale(targetLocale ?? '')
    console.debug(`Set locale from usersLanguage to ${next}`)
    i18nDef.global.locale.value = next
  })
})
